import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, toDisplayString as _toDisplayString, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createStaticVNode as _createStaticVNode } from "vue"

const _hoisted_1 = {
  key: 0,
  class: ""
}
const _hoisted_2 = { class: "relative px-6 md:px-12 pt-14 mt-0 overflow-hidden pb-2 md:pb-12" }
const _hoisted_3 = { class: "max-w-screen-2xl mx-auto relative z-20 md:bg-white md:bg-opacity-70 md:border md:border-solid border-black rounded-md px-4 md:px-12 py-8 mt-10 flex flex-col md:flex-row justify-between items-end" }
const _hoisted_4 = { class: "flex flex-col" }
const _hoisted_5 = { class: "pt-6 font-poppins text-black text-lg my-0" }
const _hoisted_6 = { class: "w-full md:w-auto" }
const _hoisted_7 = {
  key: 0,
  class: "px-4 md:px-12 max-w-screen-2xl mx-auto"
}
const _hoisted_8 = { class: "grid grid-cols-1 md:grid-cols-3 z-30 relative pt-6" }
const _hoisted_9 = { class: "hidden md:block text-right col-span-1 col-start-3" }

import StepGuide from '../components/ordering/StepGuide.vue';
import OrderForm, { ContactInformation } from '../components/ordering/OrderForm.vue';
import { computed, onMounted, Ref, ref, reactive } from 'vue';
import store from '@/store';
import { injectStrict } from '@/plugins/vueServiceContainerPlugin';
import { findPlanAction } from '@/utils/promotionActions';
import { isSamePhone } from '@/utils/cartLoader';
import { Catalog, CatalogAdapterInterface, Product } from '@/adapters/catalogAdapterInterface';
import router from '@/router';
import { Customer, OrderServiceInterface, ValidationError } from '@/services/orderServiceInterface';
import { ShoppingCartClientInterface } from '@/services/integration/clients/shoppingCartClientInterface';
import { Lead } from '@/services/integration/clients/types';
import { BrandServiceInterface } from '@/services/brand/brandServiceInterface';
import { PromotionsHandlerInterface } from '@/services/promotionsHandlerInterface';
import { Action, Promotion } from '@uniti/promotions-js-client/dist/utilities/types';
import { useGtm } from '@gtm-support/vue-gtm';

// Injections.

export default /*@__PURE__*/_defineComponent({
  __name: 'ConfirmOrder',
  setup(__props) {

const brandService = injectStrict<BrandServiceInterface>('BrandServiceInterface');
const catalogAdapter = injectStrict<CatalogAdapterInterface>('CatalogAdapterInterface');
const orderService = injectStrict<OrderServiceInterface>('OrderServiceInterface');
const shoppingCartService = injectStrict<ShoppingCartClientInterface>('ShoppingCartClientInterface');
const promotionsHandler = injectStrict<PromotionsHandlerInterface>('PromotionsHandlerInterface');

// Data.
const catalogs: Ref<Array<Catalog>> = ref([]);
const serviceDetails = reactive({
  rtcDays: 0,
  relocation: 0,
  newService: 0
});

// Promotions
const suggestedPromotion: Ref<Promotion|undefined> = ref();
const validPromotion: Ref<Promotion|undefined> = ref();
const promotionDiscounts: Ref<Array<Action>> = ref([]);
const promoCode: Ref<string|undefined> = ref();
const isPromoValid: Ref<boolean> = ref(false);

// Products chosen on the sign-up page.
const selectedProducts: Ref<Array<Product>> = ref([]);

const ready = ref(false);
const validationErrors = ref<string[]>([]);
const cartId = ref<string|undefined>('');
const crmId = ref<string|null>(null);

const gtm = useGtm();

const otpStep = ref(1);

const closeOtp = () => {
  otpStep.value = 1;
};

const goToOtp = () => {
  otpStep.value = 2;
};

const goToPayment = () => {
  otpStep.value = 3;
};

// Computed properties.
const serviceQualification = computed(() => {
  // Get SQ from store.
  return store.state.serviceQualification;
});

// Cart opened with ?cartId= that is being edited.
const loadedCart = computed(() => store.state.loadedCart);

// The cart for this order: a loaded cart, or one saved on this page by Confirm Contact.
const orderCartId = computed(() => loadedCart.value?.id || store.state.cartId || undefined);

// Keep the cart when going back to change the plan.
const signUpLink = computed(() => {
  return { path: '/sign-up', query: orderCartId.value ? { cartId: orderCartId.value } : {} };
});

// The cart id is in the URL as ?id= (?cartId= still works for older links).
const cartIdFromUrl = (): string | undefined => {
  const query = router.currentRoute.value.query;
  const id = query.id ?? query.cartId;
  return typeof id === 'string' && id ? id : undefined;
};

// Hooks.
onMounted(async () => {
  const orderSelection = store.state.orderSelection;
  const cartIdParam = cartIdFromUrl();

  if (cartIdParam && (orderCartId.value !== cartIdParam || !orderSelection)) {
    // The cart is loaded (and its products selected) on the sign-up page.
    router.push({ path: '/sign-up', query: { cartId: cartIdParam } });
    return;
  }

  if (!serviceQualification.value) {
    // If SQ hasn't been done, we can't use this page.
    router.push('/');
    return;
  }

  if (!orderSelection || !orderSelection.selectedProducts.some(product => product.type === 'internet')) {
    // A plan has to be picked before the order can be completed.
    router.push(signUpLink.value);
    return;
  }

  // Save to this order's cart rather than creating a new one.
  if (orderCartId.value) {
    cartId.value = orderCartId.value;
  }
  if (loadedCart.value) {
    crmId.value = loadedCart.value.crmId;
  }

  // Get catalogs for brand
  catalogs.value = await catalogAdapter.getCatalog(brandService.getName());

  selectedProducts.value = [...orderSelection.selectedProducts];
  await updatePromotion(orderSelection.promoCode);

  // Display user interface now.
  ready.value = true;
});

const promoCodeApplied = (promoCodeInput?: string) => {
  updatePromotion(promoCodeInput);
};

const updatePromotion = async (code?: string) => {
  promoCode.value = code;
  suggestedPromotion.value = undefined;

  if (serviceQualification.value && code) {
    suggestedPromotion.value = await promotionsHandler.suggest(serviceQualification.value, brandService.getName(), catalogs.value, code);
  }

  // Remove all the existing discounts so we can re-calculate them for a new promotion.
  selectedProducts.value = promotionsHandler.removeDiscountsFromProducts(selectedProducts.value);

  // If suggestedPromotion exists modify products based on the promotion actions
  if (suggestedPromotion.value) {
    selectedProducts.value = promotionsHandler.mapActionsToProducts(suggestedPromotion.value, selectedProducts.value);
  }

  await applyPromotion();
};

const applyPromotion = async () => {
  if (serviceQualification.value && promoCode.value) {
    validPromotion.value = await promotionsHandler.validate(serviceQualification.value, selectedProducts.value, brandService.getName(), promoCode.value, catalogs.value, serviceDetails);
  } else {
    validPromotion.value = undefined;
  }
  // Set the validity of the promotion
  isPromoValid.value = Boolean(validPromotion.value);

  // Set if the selected contract / plan is valid - this is done so we can still display discounts on a partially validated promotion
  let isContractValid = false;
  let isPlanValid = false;
  if (promoCode.value) {
    isContractValid = await promotionsHandler.isContractLengthValid(selectedProducts.value, promoCode.value, brandService.getName());
    isPlanValid = await promotionsHandler.isPlanValid(selectedProducts.value, promoCode.value, brandService.getName());
  }

  // If the promotion is valid we check to see if there is a delayedNdc or Free Year action on the promotion. This is used to render
  // promotional descriptions in the order summary.
  promotionDiscounts.value = [];
  if (isPromoValid.value) {
    const ndcProduct = selectedProducts.value.find(product => product.code === 'NDC');
    const internetProduct = selectedProducts.value.find(product => product.type === 'internet');

    if (ndcProduct) {
      const delayedNdcAction = validPromotion.value?.actions.find(action => action.actionCatalogObject.code === 'DELAYED-NDC');
      if (delayedNdcAction) promotionDiscounts.value.push(delayedNdcAction);
    }

    if (internetProduct) {
      // Plan-based actions only show when they apply to the selected plan.
      const yearOffAction = findPlanAction(validPromotion.value, 'YEAR-OFF', selectedProducts.value);
      if (yearOffAction) promotionDiscounts.value.push(yearOffAction);

      const oneOffAction = findPlanAction(validPromotion.value, 'ONE-OFF', selectedProducts.value);
      if (oneOffAction) promotionDiscounts.value.push(oneOffAction);
    }

    const freePreInstalledRouterAction = validPromotion.value?.actions.find(action => action.actionCatalogObject.code === 'FREE-ROUTER-INSTALLED');
    if (freePreInstalledRouterAction) promotionDiscounts.value.push(freePreInstalledRouterAction);
  }

  const hardware = selectedProducts.value.filter(product => product.type === 'hardware');
  if (isPromoValid.value && validPromotion.value) {
    // Apply the discounts using applicable when Id's
    promotionsHandler.applyApplicableWhenConditionToProducts(validPromotion.value, selectedProducts.value, hardware);
  }

  // Set the discounts to display depending on the the promo / plan / contract.
  // Same mapping as the sign-up page: promo -> hardware, plan -> fees, contract -> plans.
  const fees = selectedProducts.value.filter(product => product.type === 'fee');
  const plans = selectedProducts.value.filter(product => product.type === 'internet');
  if (isPromoValid.value) {
    promotionsHandler.addDisplayDiscountConditionToProducts(hardware);
  } else {
    promotionsHandler.removeDisplayDiscountConditionFromProducts(hardware);
  }
  if (isPlanValid) {
    promotionsHandler.addDisplayDiscountConditionToProducts(fees);
  } else {
    promotionsHandler.removeDisplayDiscountConditionFromProducts(fees);
  }
  if (isContractValid) {
    promotionsHandler.addDisplayDiscountConditionToProducts(plans);
  } else {
    promotionsHandler.removeDisplayDiscountConditionFromProducts(plans);
  }

  // New array so the order summary re-renders with the updated discounts.
  selectedProducts.value = [...selectedProducts.value];
};

const confirmContact = async (contactInformation: Customer, done: () => void) => {
  validationErrors.value = [];

  if (serviceQualification.value) {
    // Add the crmId to the rest of the order data
    if (crmId.value) {
      contactInformation.crmId = crmId.value;
    }
    try {
      // Save cart.
      cartId.value = await orderService.submitOrderToShoppingCart(serviceQualification.value, selectedProducts.value, contactInformation, isPromoValid.value, validPromotion.value, cartId.value);
      store.commit('setCartId', cartId.value);
      // Skip OTP when the loaded cart's phone was already verified and hasn't changed.
      const phoneAlreadyVerified = Boolean(loadedCart.value?.phoneVerified) && isSamePhone(contactInformation.phoneNumber, loadedCart.value?.customer.phoneNumber);

      // Put the cart in the URL for the payment step (replace, so Back doesn't stop on the old URL).
      if (cartId.value && cartIdFromUrl() !== cartId.value) {
        router.replace({ path: '/confirm-order', query: { id: cartId.value } });
      }
      if (phoneAlreadyVerified) {
        goToPayment();
      } else {
        goToOtp();
      }
      done();
    } catch (error) {
      console.log('error', error);
      done();
    }
  }
};

const confirmOrder = async (confirmOrderData: Customer, done: () => void) => {
  validationErrors.value = [];
  if (serviceQualification.value) {
    // Add the crmId to the rest of the order data
    if (crmId.value) {
      confirmOrderData.crmId = crmId.value;
    }

    try {
      // Save cart.
      cartId.value = await orderService.submitOrderToShoppingCart(serviceQualification.value, selectedProducts.value, confirmOrderData, isPromoValid.value, validPromotion.value, cartId.value);

      // Save cart with payment. Two steps so
      // that if payment fails we don't create duplicate carts.
      cartId.value = await orderService.submitOrderToShoppingCartWithMonoovaPayment(serviceQualification.value, selectedProducts.value, confirmOrderData, isPromoValid.value, validPromotion.value, cartId.value);

      const gtmItems: [Record<string, unknown>] = [{}];
      // create product items for GA
      selectedProducts.value.map((pr) => {
        const tempItem = {
          item_name: pr.name,
          item_id: pr.id,
          price: pr.price,
          item_brand: pr.brand,
          item_category: pr.type,
          quantity: 1
        };
        gtmItems.push(tempItem);
      });

      // Save to GA
      if (gtm) {
        const dataLayer = gtm.dataLayer();
        if (dataLayer) {
          dataLayer.push({ ecommerce: null });
          dataLayer.push({
            event: 'purchase',
            ecommerce: {
              currency: 'AUD',
              value: orderService.calculateFirstInvoiceTotal(selectedProducts.value, isPromoValid.value, validPromotion.value),
              tax: 0,
              shipping: 0,
              affiliation: brandService.getName(),
              transaction_id: cartId.value,
              coupon: validPromotion.value ? validPromotion.value.code : '',
              items: gtmItems
            }
          });
        }
      }
      // Redirect.
      store.commit('setServiceQualification', null);
      store.commit('setOrderSelection', null);
      store.commit('setLoadedCart', null);
      router.push({ path: '/confirm', query: cartId.value ? { id: cartId.value } : {} });
      done();
    } catch (error) {
      if (error instanceof ValidationError) {
        validationErrors.value.push(error.message);
      }
      done();
    }
  }
};

const createUpdateLead = async (contactInformation: ContactInformation): Promise<void> => {
  if (serviceQualification.value) {
    const lead : Lead = {
      email: contactInformation.email,
      brand: brandService.getName(),
      carrier: serviceQualification.value.carrier,
      carrierId: serviceQualification.value.locationId,
      firstName: contactInformation.firstName,
      lastName: contactInformation.lastName,
      technology: serviceQualification.value.technology,
      phone: contactInformation.phoneNumber,
      address: serviceQualification.value.address
    };

    crmId.value = await shoppingCartService.updateLead(lead);
  }
};

return (_ctx: any,_cache: any) => {
  const _component_router_link = _resolveComponent("router-link")!

  return (serviceQualification.value)
    ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
        _createElementVNode("div", _hoisted_2, [
          _cache[2] || (_cache[2] = _createStaticVNode("<img src=\"https://uniti-bpo-website-assets.assets.unitiinternet.com/NEX/orderbg.png\" alt=\"\" class=\"absolute lg:inset-0 -inset-x-8 z-0 object-cover object-center w-auto lg:h-full h-96 lg:w-full\"><div class=\"relative z-20 pt-16 max-w-screen-2xl mx-auto\"><h1 class=\"font-benzin text-white text-4xl font-light tracking-wide leading-tight pt-2 md:pt-20\">CONNECT <br> TO <span class=\"text-brand-primary\">NEXEE</span></h1><p class=\"font-poppins max-w-md text-white\">You&#39;re almost done! First, we&#39;ll need a few details from you to connect your new Nexee internet service.</p></div>", 2)),
          _createElementVNode("div", _hoisted_3, [
            _createElementVNode("div", _hoisted_4, [
              _cache[0] || (_cache[0] = _createElementVNode("h2", { class: "text-black font-poppins-bold text-2xl py-0 my-0" }, "This is your address:", -1)),
              _createElementVNode("p", _hoisted_5, _toDisplayString(serviceQualification.value.address) + ".", 1)
            ]),
            _createElementVNode("div", _hoisted_6, [
              _createVNode(_component_router_link, { to: signUpLink.value }, {
                default: _withCtx(() => _cache[1] || (_cache[1] = [
                  _createElementVNode("button", { class: "mt-4 md:mt-0 transition duration-250 rounded-full hover:bg-white hover:border-solid hover:border-black bg-transparent bg-gradient-to-tr from-brand-primary-light to-brand-primary-dark py-3 px-6 shadow-md text-center font-poppins text-black border-none text-lg w-full md:w-auto cursor-pointer" }, "Change My Plan", -1)
                ])),
                _: 1,
                __: [1]
              }, 8, ["to"])
            ])
          ])
        ]),
        (ready.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
              _createElementVNode("div", _hoisted_8, [
                _cache[3] || (_cache[3] = _createElementVNode("div", { class: "text-left col-start-1 flex flex-col col-span-2" }, [
                  _createElementVNode("p", { class: "font-benzin text-black text-2xl tracking-normal font-normal py-0 my-0" }, [
                    _createTextVNode("COMPLETE YOUR"),
                    _createElementVNode("span", { class: "text-brand-primary" }, " ORDER")
                  ]),
                  _createElementVNode("p", { class: "text-lg text-black font-poppins tracking-normal pt-2 py-0 my-0" }, "Enter your personal details to finalise your internet service!")
                ], -1)),
                _createElementVNode("div", _hoisted_9, [
                  _createVNode(StepGuide, { "current-step": 5 })
                ])
              ]),
              _createVNode(OrderForm, {
                onOnConfirmOrder: confirmOrder,
                onOnConfirmContact: confirmContact,
                onOnContactInformation: createUpdateLead,
                onOnEnterPromoCode: promoCodeApplied,
                errors: validationErrors.value,
                service: serviceQualification.value,
                products: selectedProducts.value,
                isPromoValid: isPromoValid.value,
                promotionDiscounts: promotionDiscounts.value,
                suggestedPromotion: suggestedPromotion.value,
                validPromotion: validPromotion.value,
                promoCode: promoCode.value,
                initialCustomer: loadedCart.value?.customer,
                otpStep: otpStep.value,
                onOnOtpVerified: goToPayment,
                onCloseOtp: closeOtp,
                class: "mb-4 md:mb-20 mt-10"
              }, null, 8, ["errors", "service", "products", "isPromoValid", "promotionDiscounts", "suggestedPromotion", "validPromotion", "promoCode", "initialCustomer", "otpStep"])
            ]))
          : _createCommentVNode("", true)
      ]))
    : _createCommentVNode("", true)
}
}

})